/**
 * The pieces the NDT AI pages draw (batch 6, fieldiq-api#1177 / #1178): the
 * deterministic figures of each digest as tables, and the AI draft with its
 * review buttons. Used by the digest page and by the ticket page's Preflight.
 *
 * THE FIGURES ARE THE API'S DETERMINISTIC LAYER, shown first and in full. The
 * AI wrote only the draft text below them, and it reads "Draft — needs review"
 * until a person approves it through the shared /agent-runs doors. A run whose
 * draft failed still has correct figures, and says so.
 *
 * Every value that did not arrive renders `—`; a real 0 renders `0`.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useNdtAiDigest, useReviewNdtAi } from '../../hooks/useNdtAi';
import {
  ndtAiDoseSkin,
  ndtAiItemSkin,
  ndtDaysLeftText,
  ndtDoseText,
  ndtSurveyRateText,
  ndtPointsText,
  ndtRepairFlagText,
} from '../../lib/ndt-ai';
import { formatRepairRate, ndtCycleLabel, ndtDosimeterLabel, ndtErrorMessage, ndtEventLabel, ndtExamResultSkin } from '../../lib/ndt-inspection';
import { ndtLineUnitLabel } from '../../lib/ndt-tickets';
import type {
  NdtAiRunResult,
  NdtCertExpiryDigest,
  NdtDigestDraft,
  NdtDoseDraft,
  NdtDoseReview,
  NdtExpiryItem,
  NdtPreflight,
  NdtRateGroup,
  NdtRepairFlag,
  NdtRepairRateDigest,
  NdtSurveyAnomaly,
  NdtWorkerDose,
} from '../../types/ndt-ai';
import { NDT_PRIMARY, NDT_SECONDARY, NdtBadge, NdtEmpty, NdtPanel, NdtSpec, ndtDay } from './NdtBits';

const arr = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

// ─── A small read-only table ─────────────────────────────────────────────────

interface Col<T> {
  key: string;
  header: string;
  render: (row: T) => React.ReactNode;
  align?: 'right';
}

function AiTable<T>({ rows, cols, rowKey, rail, empty, testId, minWidth = 'min-w-[640px]' }: {
  rows: T[];
  cols: Col<T>[];
  rowKey: (row: T, i: number) => string;
  rail?: (row: T) => string;
  empty: string;
  testId: string;
  minWidth?: string;
}) {
  if (rows.length === 0) return <NdtEmpty>{empty}</NdtEmpty>;
  return (
    <div className="overflow-x-auto">
      <table className={`w-full ${minWidth} text-left`} data-testid={testId}>
        <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
          <tr className="border-l-4 border-l-transparent">
            {cols.map((c) => (
              <th key={c.key} scope="col" className={`px-4 py-3 ${c.align === 'right' ? 'text-right' : ''}`}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={rowKey(r, i)} data-testid={`${testId}-row`} className={`border-t border-l-4 border-fieldiq-border ${rail ? rail(r) : 'border-l-transparent'}`}>
              {cols.map((c) => (
                <td key={c.key} className={`px-4 py-3 text-sm text-fieldiq-text ${c.align === 'right' ? 'text-right font-mono tabular-nums' : ''}`}>
                  {c.render(r)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const FLAG_SKIN = { label: 'Flagged', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' };
const SMALL_SKIN = { label: 'Small sample', fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' };

// ─── Repair-rate digest ──────────────────────────────────────────────────────

function rateCols(flagged: (g: NdtRateGroup) => boolean, first: string): Col<NdtRateGroup>[] {
  return [
    { key: 'name', header: first, render: (g) => <span className="font-semibold text-white">{g.label || '—'}</span> },
    { key: 'examined', header: 'Examined', align: 'right', render: (g) => displayValue(g.examined) },
    { key: 'repaired', header: 'Repaired', align: 'right', render: (g) => displayValue(g.repaired) },
    { key: 'rate', header: 'Repair rate', align: 'right', render: (g) => formatRepairRate(g.rate) },
    {
      key: 'flag',
      header: 'Standing',
      render: (g) =>
        flagged(g) ? <NdtBadge skin={FLAG_SKIN} value="flagged" /> : g.smallSample ? <NdtBadge skin={SMALL_SKIN} value="small_sample" /> : '—',
    },
  ];
}

export function RepairRateFigures({ data }: { data: NdtRepairRateDigest }) {
  const flags = arr<NdtRepairFlag>(data.flags);
  const isFlagged = (kind: string) => (g: NdtRateGroup) => flags.some((f) => f.kind === kind && f.key === g.key);
  const railFor = (kind: string) => (g: NdtRateGroup) => (isFlagged(kind)(g) ? 'border-l-fi-red' : 'border-l-transparent');
  const min = data.smallSampleWelds;
  return (
    <div className="space-y-4" data-testid="ndt-ai-repair-figures">
      <NdtPanel eyebrow="Repair rate" title="Overall">
        <dl className="grid gap-4 px-4 py-4 sm:grid-cols-4">
          <NdtSpec label="Window" value={`${ndtDay(data.from)} – ${ndtDay(data.to)}`} />
          <NdtSpec label="Welds examined" value={displayValue(data.overall?.examined)} mono />
          <NdtSpec label="Repaired" value={displayValue(data.overall?.repaired)} mono />
          <NdtSpec label="Repair rate" value={formatRepairRate(data.overall?.rate)} mono />
        </dl>
        <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted" data-testid="ndt-ai-small-sample-note">
          A group with fewer than {displayValue(min)} examined welds is listed but never compared or flagged.
        </p>
      </NdtPanel>

      <NdtPanel eyebrow="Flags" title="What stands out" testId="ndt-ai-repair-flags">
        <AiTable<NdtRepairFlag>
          rows={flags}
          testId="ndt-ai-flag"
          rowKey={(f) => `${f.kind}:${f.key}`}
          rail={() => 'border-l-fi-red'}
          empty="Nothing was flagged in this window."
          cols={[
            { key: 'kind', header: 'Flag', render: (f) => ndtRepairFlagText(f.kind) },
            { key: 'who', header: 'Subject', render: (f) => <span className="font-semibold text-white">{f.label || '—'}</span> },
            { key: 'rate', header: 'Rate', align: 'right', render: (f) => `${formatRepairRate(f.rate)} (${displayValue(f.repaired)} of ${displayValue(f.examined)})` },
            {
              key: 'vs',
              header: 'Compared with',
              render: (f) => `${f.comparedTo?.label || '—'}: ${formatRepairRate(f.comparedTo?.rate)} (${displayValue(f.comparedTo?.repaired)} of ${displayValue(f.comparedTo?.examined)})`,
            },
            { key: 'pts', header: 'Higher by', align: 'right', render: (f) => ndtPointsText(f.points) },
          ]}
        />
      </NdtPanel>

      <NdtPanel eyebrow="Repair rate" title="By welder" testId="ndt-ai-by-welder">
        <AiTable rows={arr<NdtRateGroup>(data.byWelder)} cols={rateCols(isFlagged('welder_above_peers'), 'Welder')} rowKey={(g) => g.key} rail={railFor('welder_above_peers')} testId="ndt-ai-welder" empty="No examined welds name a welder." />
      </NdtPanel>
      <NdtPanel eyebrow="Repair rate" title="By line" testId="ndt-ai-by-line">
        <AiTable rows={arr<NdtRateGroup>(data.byLine)} cols={rateCols(isFlagged('line_above_peers'), 'Line')} rowKey={(g) => g.key} rail={railFor('line_above_peers')} testId="ndt-ai-line" empty="No examined welds name a line." />
      </NdtPanel>
      <NdtPanel eyebrow="Repair rate" title="By month" testId="ndt-ai-by-month">
        <AiTable rows={arr<NdtRateGroup>(data.byMonth)} cols={rateCols(isFlagged('month_increase'), 'Month')} rowKey={(g) => g.key} rail={railFor('month_increase')} testId="ndt-ai-month" empty="No welds were examined in this window." />
      </NdtPanel>
    </div>
  );
}

// ─── Compliance digest ───────────────────────────────────────────────────────

const itemCols: Col<NdtExpiryItem>[] = [
  { key: 'name', header: 'Name', render: (i) => <span className="font-semibold text-white">{i.name || '—'}</span> },
  { key: 'what', header: 'Item', render: (i) => i.detail || '—' },
  { key: 'status', header: 'Status', render: (i) => <NdtBadge skin={ndtAiItemSkin(i.status)} value={i.status} /> },
  { key: 'day', header: 'Date', render: (i) => ndtDay(i.day) },
  { key: 'left', header: 'Days left', align: 'right', render: (i) => ndtDaysLeftText(i.daysLeft) },
];

export function ComplianceFigures({ data }: { data: NdtCertExpiryDigest }) {
  const lists: { key: string; title: string; rows: NdtExpiryItem[]; empty: string }[] = [
    { key: 'certs', title: 'Technician certifications', rows: arr(data.techCerts), empty: 'No technician certification is expired or due.' },
    { key: 'quals', title: 'Welder qualifications', rows: arr(data.welderQuals), empty: 'No welder qualification is expired, lapsed or due.' },
    { key: 'leak', title: 'Source leak tests', rows: arr(data.leakTests), empty: 'No source leak test is overdue or due.' },
    { key: 'cal', title: 'Instrument calibrations', rows: arr(data.calibrations), empty: 'No instrument calibration is overdue, due or missing.' },
  ];
  return (
    <div className="space-y-4" data-testid="ndt-ai-compliance-figures">
      <p className="text-sm text-fieldiq-text">
        As of {ndtDay(data.today)}: <span className="font-bold">{displayValue(data.total)}</span> item{data.total === 1 ? '' : 's'} need attention.
      </p>
      {lists.map((l) => (
        <NdtPanel key={l.key} eyebrow="Compliance" title={`${l.title} (${l.rows.length})`} testId={`ndt-ai-compliance-${l.key}`}>
          <AiTable rows={l.rows} cols={itemCols} rowKey={(i) => i.id} rail={(i) => ndtAiItemSkin(i.status).rail} testId={`ndt-ai-item-${l.key}`} empty={l.empty} />
        </NdtPanel>
      ))}
    </div>
  );
}

// ─── Dose review ─────────────────────────────────────────────────────────────

type Units = 'metric' | 'imperial' | null | undefined;

const anomalyCols = (withWorker: boolean, units: Units): Col<NdtSurveyAnomaly>[] => [
  ...(withWorker ? [{ key: 'who', header: 'Worker', render: (a: NdtSurveyAnomaly) => a.userName || '—' }] : []),
  { key: 'source', header: 'Source', render: (a) => a.source || '—' },
  { key: 'day', header: 'Day', render: (a) => ndtDay(a.day) },
  { key: 'event', header: 'Event', render: (a) => ndtEventLabel(a.event) },
  { key: 'reading', header: 'Reading', align: 'right', render: (a) => ndtSurveyRateText(a.readingUsvH, units) },
  { key: 'median', header: 'Usual (median)', align: 'right', render: (a) => `${ndtSurveyRateText(a.peerMedianUsvH, units)} of ${displayValue(a.peerReadings)}` },
  { key: 'ratio', header: 'Times usual', align: 'right', render: (a) => (typeof a.ratio === 'number' ? `${a.ratio}×` : '—') },
];

export function DoseFigures({ data, unitSystem }: { data: NdtDoseReview; unitSystem: Units }) {
  const workers = arr<NdtWorkerDose>(data.workers);
  const anomalies = workers.flatMap((w) => arr<NdtSurveyAnomaly>(w.surveyAnomalies).map((a) => ({ ...a, userName: a.userName ?? w.name })));
  const unattributed = arr<NdtSurveyAnomaly>(data.unattributedAnomalies);
  const sv = data.survey;
  return (
    <div className="space-y-4" data-testid="ndt-ai-dose-figures">
      <NdtPanel eyebrow="Dose review" title="Investigation level">
        <dl className="grid gap-4 px-4 py-4 sm:grid-cols-4">
          <NdtSpec label="Window" value={`${ndtDay(data.from)} – ${ndtDay(data.to)}`} />
          <div className="min-w-0" data-testid="ndt-ai-dose-level">
            <NdtSpec label="Investigation level" value={data.levelConfigured ? ndtDoseText(data.investigationLevelMsv, unitSystem) : 'Not set'} mono />
          </div>
          <NdtSpec label="Workers with records" value={displayValue(workers.length)} mono />
          <NdtSpec label="Flagged" value={displayValue(data.flaggedWorkers)} mono />
        </dl>
        {!data.levelConfigured && (
          <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-text" data-testid="ndt-ai-no-level">
            No investigation level is set in NDT settings, so no worker is flagged on dose. Survey anomalies are still checked.
          </p>
        )}
        <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
          {unitSystem
            ? `Doses in ${unitSystem === 'imperial' ? 'mrem' : 'mSv'} and survey readings in ${unitSystem === 'imperial' ? 'mR/h' : 'µSv/h'}, the company's units.`
            : 'Doses in mSv and survey readings in µSv/h: the company\'s units could not be read.'}{' '}
          A record counts as approaching at{' '}
          {typeof data.approachFraction === 'number' ? `${Math.round(data.approachFraction * 100)}%` : '—'} of the level.
        </p>
      </NdtPanel>

      <NdtPanel eyebrow="Dose review" title="Workers" testId="ndt-ai-dose-workers">
        <AiTable<NdtWorkerDose>
          rows={workers}
          testId="ndt-ai-worker"
          rowKey={(w) => w.userId}
          rail={(w) => ndtAiDoseSkin(w.status).rail}
          empty="No dose records end in this window."
          minWidth="min-w-[760px]"
          cols={[
            { key: 'name', header: 'Worker', render: (w) => <span className="font-semibold text-white">{w.name || '—'}</span> },
            {
              key: 'records',
              header: 'Records',
              render: (w) => (
                <ul className="space-y-0.5 text-xs">
                  {arr<NdtWorkerDose['records'][number]>(w.records).map((r) => (
                    <li key={r.id}>
                      {ndtDay(r.periodStart)} – {ndtDay(r.periodEnd)} · {ndtDosimeterLabel(r.dosimeterKind)} · {ndtDoseText(r.deepDoseMsv, unitSystem)}
                    </li>
                  ))}
                </ul>
              ),
            },
            { key: 'max', header: 'Highest record', align: 'right', render: (w) => ndtDoseText(w.maxDeepMsv, unitSystem) },
            { key: 'total', header: 'Window total', align: 'right', render: (w) => ndtDoseText(w.totalDeepMsv, unitSystem) },
            { key: 'status', header: 'Dose', render: (w) => <NdtBadge skin={ndtAiDoseSkin(w.status)} value={w.status ?? 'unset'} /> },
            { key: 'anom', header: 'Survey anomalies', align: 'right', render: (w) => displayValue(arr(w.surveyAnomalies).length) },
          ]}
        />
      </NdtPanel>

      <NdtPanel
        eyebrow="Survey readings"
        title="Anomalies"
        testId="ndt-ai-anomalies"
        footer={
          sv
            ? `${displayValue(sv.readings)} readings in the window, ${displayValue(sv.compared)} compared, ${displayValue(sv.notCompared)} not compared (fewer than ${displayValue(sv.minPeerReadings)} other readings of the same source). A reading is flagged at ${displayValue(sv.ratio)}× the usual.`
            : undefined
        }
      >
        <AiTable rows={anomalies} cols={anomalyCols(true, unitSystem)} rowKey={(a) => a.logId} rail={() => 'border-l-fi-amber'} testId="ndt-ai-anomaly" empty="No survey reading stood out." />
        {unattributed.length > 0 && (
          <div className="border-t border-fieldiq-border" data-testid="ndt-ai-unattributed">
            <p className="px-4 pt-3 text-xs font-bold text-fieldiq-text">With no worker recorded</p>
            <AiTable rows={unattributed} cols={anomalyCols(false, unitSystem)} rowKey={(a) => a.logId} rail={() => 'border-l-fi-amber'} testId="ndt-ai-unattributed-anomaly" empty="" />
          </div>
        )}
      </NdtPanel>
    </div>
  );
}

// ─── Ticket preflight ────────────────────────────────────────────────────────

const READY_SKIN = { label: 'Ready', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' };
const NOT_READY_SKIN = { label: 'Not ready', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' };

export function PreflightFigures({ data }: { data: NdtPreflight }) {
  const unpriced = arr<NdtPreflight['unpricedLines'][number]>(data.unpricedLines);
  const unclaimed = arr<NdtPreflight['unclaimedExams'][number]>(data.unclaimedExams);
  return (
    <div className="space-y-4" data-testid="ndt-ai-preflight-figures">
      <NdtPanel eyebrow="Preflight" title={data.ticket?.ticketNumber || 'Field ticket'}>
        <dl className="grid gap-4 px-4 py-4 sm:grid-cols-4">
          <div className="min-w-0">
            <dt className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Ready to send</dt>
            <dd className="mt-1" data-testid="ndt-ai-preflight-ready">
              {typeof data.ready === 'boolean' ? <NdtBadge skin={data.ready ? READY_SKIN : NOT_READY_SKIN} value={String(data.ready)} /> : '—'}
            </dd>
          </div>
          <NdtSpec label="Unpriced lines" value={displayValue(unpriced.length)} mono />
          <NdtSpec label="Unbilled exams" value={displayValue(unclaimed.length)} mono />
          <div className="min-w-0" data-testid="ndt-ai-preflight-pending">
            <NdtSpec label="Exams pending a result" value={displayValue(data.pendingExams)} mono />
          </div>
        </dl>
      </NdtPanel>
      <NdtPanel eyebrow="Preflight" title="Unpriced lines" testId="ndt-ai-unpriced">
        <AiTable
          rows={unpriced}
          testId="ndt-ai-unpriced-line"
          rowKey={(l) => l.id}
          rail={() => 'border-l-fi-amber'}
          empty="Every line on this ticket has a price."
          cols={[
            { key: 'line', header: 'Line', render: (l) => <span className="font-semibold text-white">{l.description || '—'}</span> },
            { key: 'unit', header: 'Unit', render: (l) => ndtLineUnitLabel(l.unit) },
            { key: 'day', header: 'Day', render: (l) => ndtDay(l.workDate) },
            { key: 'why', header: 'Why', render: (l) => l.reason || 'No rate recorded' },
          ]}
        />
      </NdtPanel>
      <NdtPanel eyebrow="Preflight" title="Exams on no ticket" testId="ndt-ai-unclaimed">
        <AiTable
          rows={unclaimed}
          testId="ndt-ai-unclaimed-exam"
          rowKey={(e) => e.id}
          rail={() => 'border-l-fi-amber'}
          empty="Every interpreted exam on this job and day is on a ticket."
          cols={[
            { key: 'weld', header: 'Weld', render: (e) => <span className="font-mono">{e.weldNumber || '—'}</span> },
            { key: 'method', header: 'Method', render: (e) => e.method || '—' },
            { key: 'cycle', header: 'Cycle', render: (e) => ndtCycleLabel(e.cycle) },
            { key: 'result', header: 'Result', render: (e) => <NdtBadge skin={ndtExamResultSkin(e.result)} value={e.result} /> },
            { key: 'day', header: 'Day', render: (e) => ndtDay(e.workDate) },
          ]}
        />
        <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">An exam with no rate on the card is not billed; that may be deliberate.</p>
      </NdtPanel>
    </div>
  );
}

/** The figures for any digest, by agent. Unknown agent or missing data says so rather than guessing. */
export function DigestFigures({ agent, data, unitSystem }: { agent: string; data: unknown; unitSystem?: Units }) {
  if (!data || typeof data !== 'object') {
    return <p className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-4 text-sm text-fieldiq-text">The figures for this run were not stored.</p>;
  }
  if (agent === 'ndt_repair_rate_digest') return <RepairRateFigures data={data as NdtRepairRateDigest} />;
  if (agent === 'ndt_cert_expiry_digest') return <ComplianceFigures data={data as NdtCertExpiryDigest} />;
  if (agent === 'ndt_dose_review') return <DoseFigures data={data as NdtDoseReview} unitSystem={unitSystem} />;
  if (agent === 'ndt_ticket_preflight') return <PreflightFigures data={data as NdtPreflight} />;
  return <p className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-4 text-sm text-fieldiq-text">This digest type is not shown here.</p>;
}

// ─── The AI draft and its review ─────────────────────────────────────────────

const DRAFT_HEADING: Record<string, string> = {
  pending: 'Draft — needs review',
  actioned: 'Draft — approved',
  dismissed: 'Draft — dismissed',
  superseded: 'Draft — superseded by a newer run',
};

function isDoseDraft(d: NdtDigestDraft | NdtDoseDraft): d is NdtDoseDraft {
  return Array.isArray((d as NdtDoseDraft).notes);
}

/**
 * The AI's text. Approve / Dismiss only on a PENDING run and only for a role
 * that may review (the API refuses anyone else and refuses a decided run with
 * a 404). A null draft is said plainly, with the API's reason.
 */
export function AiDraftPanel({
  runId,
  outcome,
  draft,
  aiError,
  canReview,
}: {
  runId: string;
  outcome: string | null | undefined;
  draft: NdtDigestDraft | NdtDoseDraft | null | undefined;
  aiError: string | null | undefined;
  canReview: boolean;
}) {
  const review = useReviewNdtAi();
  const [error, setError] = React.useState<string | null>(null);
  const pending = outcome === 'pending';
  const heading = (outcome && DRAFT_HEADING[outcome]) || 'Draft';

  const decide = (action: 'approve' | 'dismiss') => {
    setError(null);
    review.mutate(
      { id: runId, action },
      {
        onSuccess: () => toast.success(action === 'approve' ? 'Draft approved' : 'Draft dismissed'),
        onError: (err) => setError(ndtErrorMessage(err, action === 'approve' ? 'The draft was not approved.' : 'The draft was not dismissed.')),
      },
    );
  };

  return (
    <section data-testid="ndt-ai-draft" className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-blue bg-fieldiq-card">
      <header className="flex flex-wrap items-end justify-between gap-3 border-b border-fieldiq-border px-4 py-4">
        <div>
          <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">AI draft</p>
          <h2 className="mt-1 text-base font-extrabold tracking-tight text-white" data-testid="ndt-ai-draft-heading">
            {heading}
          </h2>
        </div>
        {canReview && pending && draft && (
          <div className="flex flex-wrap gap-2">
            <button type="button" className={NDT_SECONDARY} onClick={() => decide('dismiss')} disabled={review.isPending} data-testid="ndt-ai-dismiss">
              <XCircle className="size-4" aria-hidden="true" />
              Dismiss
            </button>
            <button type="button" className={NDT_PRIMARY} onClick={() => decide('approve')} disabled={review.isPending} data-testid="ndt-ai-approve">
              {review.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <CheckCircle2 className="size-4" aria-hidden="true" />}
              Approve
            </button>
          </div>
        )}
        {canReview && pending && !draft && (
          <button type="button" className={NDT_SECONDARY} onClick={() => decide('dismiss')} disabled={review.isPending} data-testid="ndt-ai-dismiss">
            <XCircle className="size-4" aria-hidden="true" />
            Dismiss
          </button>
        )}
      </header>
      {error && (
        <p role="alert" className="border-b border-fieldiq-border bg-fi-signal-red/10 px-4 py-3 text-sm text-fieldiq-text" data-testid="ndt-ai-review-error">
          {error}
        </p>
      )}
      {draft ? (
        <div className="space-y-3 px-4 py-4 text-sm text-fieldiq-text">
          <p className="whitespace-pre-wrap" data-testid="ndt-ai-narrative">{draft.narrative || '—'}</p>
          {isDoseDraft(draft) ? (
            draft.notes.length > 0 && (
              <ul className="space-y-2" data-testid="ndt-ai-notes">
                {draft.notes.map((n) => (
                  <li key={n.userId} className="rounded-fi-control border border-fieldiq-border px-3 py-2">
                    <p className="text-xs font-bold text-white">{n.workerName || '—'}</p>
                    <p className="mt-1 whitespace-pre-wrap">{n.note}</p>
                  </li>
                ))}
              </ul>
            )
          ) : (
            Array.isArray(draft.observations) &&
            draft.observations.length > 0 && (
              <ul className="list-disc space-y-1 pl-5" data-testid="ndt-ai-observations">
                {draft.observations.map((o, i) => (
                  <li key={i}>{o}</li>
                ))}
              </ul>
            )
          )}
          <p className="text-xs text-fieldiq-muted">Written by AI from the figures above. Nothing is sent or filed.</p>
        </div>
      ) : (
        <div className="space-y-1 px-4 py-4 text-sm text-fieldiq-text" data-testid="ndt-ai-no-draft">
          <p className="font-bold">AI draft unavailable — the figures above are still correct.</p>
          {aiError && <p className="text-xs text-fieldiq-muted" data-testid="ndt-ai-error">{aiError}</p>}
        </div>
      )}
    </section>
  );
}

/**
 * A preflight just run from the ticket page. The POST answers without an
 * outcome, so the stored run is read for it (and re-read after a review);
 * until it loads, a fresh run is pending by definition.
 */
export function TicketPreflightResult({ result, canReview }: { result: NdtAiRunResult<NdtPreflight>; canReview: boolean }) {
  const stored = useNdtAiDigest(result.agentRunId);
  const outcome = stored.data?.outcome ?? 'pending';
  return (
    <div className="space-y-4" data-testid="ndt-ai-preflight">
      {result.data ? <PreflightFigures data={result.data} /> : <DigestFigures agent="ndt_ticket_preflight" data={null} />}
      <AiDraftPanel runId={result.agentRunId} outcome={outcome} draft={result.draft} aiError={result.aiError} canReview={canReview} />
      <p className="text-xs text-fieldiq-muted">
        Kept with the{' '}
        <Link to={`/ndt-inspection/ai/${result.agentRunId}`} className="font-bold text-fieldiq-text underline" data-testid="ndt-ai-preflight-link">
          AI digests
        </Link>
        .
      </p>
    </div>
  );
}
